import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import UWEContext from '../../context/UWEContext';
import { AuthContext } from '../../context/AuthContext';
import { Icon } from 'src/components/ui/Icon';
import { USE_FROM_STUDIO } from '../../constants';

/**
 * Cmd/Ctrl + K command palette. Centralised launcher for navigation and
 * actions across the app — paste-pattern from Linear / Notion / GitHub.
 *
 * Sources of commands:
 *   - Tenant workpages from UWEContext (one entry per workpage slug)
 *   - System settings sections (Themes, Users, Org units, …)
 *   - Quick toggles (dark/light, logout, …)
 *
 * Keyboard:
 *   ⌘K / Ctrl+K  → open
 *   Esc          → close
 *   ↑ / ↓        → move selection
 *   Enter        → run command
 *
 * Visual: a centered modal with backdrop blur. The card uses the tenant
 * Card chrome (radius/border/shadow/bg) via the standard utilities, so
 * it re-skins when the theme changes.
 */
type Command = {
  id: string;
  label: string;
  hint?: string;
  icon?: string;
  group: string;
  run: () => void;
  /** Lower-cased search index, built once. */
  _search: string;
};

function useCommands(): Command[] {
  const history = useHistory();
  const auth = useContext(AuthContext);
  const uwe = useContext(UWEContext);

  return useMemo<Command[]>(() => {
    const cmds: Command[] = [];
    const push = (c: Omit<Command, '_search'>) =>
      cmds.push({ ...c, _search: `${c.label} ${c.hint || ''} ${c.group}`.toLowerCase() });

    // Anonymous user (login / forgot-password screens) — palette has
    // nothing useful to show. Return empty so the modal renders a clean
    // "no matches" panel rather than leaking nav targets the user has no
    // session for.
    if (!auth?.user) return cmds;

    // SAME permission gate the NavMenu uses for the system-settings gear
    // (see NavMenu.jsx: `manageAnything`). Mirrors the existing rule so
    // the palette never exposes an entry the user can't reach via the UI.
    const permissions: Record<string, unknown> = (auth?.user as any)?.permissions || {};
    const hasAdminWorkpages = !!uwe?.hasAdminWorkpages;
    const isAdmin =
      !!permissions.ManageUsersInUnit ||
      !!permissions.ManageOrganizationalUnits ||
      hasAdminWorkpages;

    // Workpages — already filtered server-side by WorkpageApi.list() to
    // those the user has access to, so we pass them through directly.
    (uwe?.workpages || []).forEach((w: any) => {
      push({
        id: `workpage:${w.slug}`,
        label: w.name || w.slug,
        hint: 'Workpage',
        icon: w?.image?.icon || 'fa fa-folder',
        group: 'Workpages',
        run: () => history.push(`/workflow/${w.slug}/tray`),
      });
    });

    // Admin workpages — server-permissioned too (UWEContext only fills
    // `adminWorkpages` for users who can see them), but we additionally
    // gate the group itself behind isAdmin so it doesn't even render the
    // section header for non-admins.
    if (isAdmin) {
      (uwe?.adminWorkpages || []).forEach((w: any) => {
        push({
          id: `admin:${w.slug}`,
          label: w.name || w.slug,
          hint: 'Admin',
          icon: w?.image?.icon || 'fa fa-cog',
          group: 'Administration',
          run: () => history.push(`/workflow/${w.slug}/tray`),
        });
      });

      // System settings shortcuts — same gate as the gear icon in the
      // header. Non-admins never see these. Each entry has a corresponding
      // route the user with `manageAnything` can navigate to today; if a
      // tenant disables ManageUsersInUnit specifically, the individual page
      // still server-guards itself but it won't 404 from this list.
      // When managed from UweStudio the settings live there — hide them all.
      if (!USE_FROM_STUDIO) {
        const sysSettings: { label: string; path: string; icon: string }[] = [
          { label: 'System settings',  path: '/system-settings',                      icon: 'fa fa-cog' },
          { label: 'Themes',           path: '/system-settings/themeEditor',               icon: 'fa fa-palette' },
          { label: 'Users',            path: '/system-settings/users',                icon: 'fa fa-users' },
          { label: 'Organizational units', path: '/system-settings/organizationalunits', icon: 'fa fa-sitemap' },
          { label: 'Roles',            path: '/system-settings/roles',                icon: 'fa fa-user-shield' },
        ];
        sysSettings.forEach((s) => {
          push({
            id: `sys:${s.path}`,
            label: s.label,
            hint: s.path,
            icon: s.icon,
            group: 'System',
            run: () => history.push(s.path),
          });
        });
      }
    }

    // Navigation — available to everyone logged in.
    push({
      id: 'go:home',
      label: 'Go to home',
      icon: 'fa fa-home',
      group: 'Navigation',
      run: () => history.push('/'),
    });

    // (Sign out intentionally omitted — auth.logout requires the MSAL
    // instance to handle SSO providers cleanly; the palette context can't
    // wire that without prop-drilling. Use the UserDropdown in the header.)

    return cmds;
  }, [uwe?.workpages, uwe?.adminWorkpages, uwe?.hasAdminWorkpages, auth, history]);
}

/** Simple subsequence fuzzy match — every char of `query` must appear in
 *  order inside `target`. Scoring rewards consecutive matches and matches
 *  at word boundaries. Good enough for ~100 commands; cheap to compute
 *  per keystroke. Returns null when no match. */
function fuzzyScore(target: string, query: string): number | null {
  if (!query) return 0;
  const t = target.toLowerCase();
  const q = query.toLowerCase();
  let score = 0;
  let ti = 0;
  let prevMatched = false;
  for (let qi = 0; qi < q.length; qi += 1) {
    const ch = q[qi];
    const found = t.indexOf(ch, ti);
    if (found === -1) return null;
    // Reward streak (consecutive chars) and word boundary (start of word).
    if (found === ti && prevMatched) score += 3;
    else if (found === 0 || t[found - 1] === ' ' || t[found - 1] === '-' || t[found - 1] === '/') score += 2;
    else score += 1;
    ti = found + 1;
    prevMatched = true;
  }
  // Shorter targets win ties.
  score -= Math.floor(t.length / 50);
  return score;
}


export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const commands = useCommands();

  // Filter + group + select first on every query change.
  const results = useMemo(() => {
    const scored = commands
      .map((c) => ({ cmd: c, score: fuzzyScore(c._search, query) }))
      .filter((s) => s.score !== null)
      .sort((a, b) => (b.score! - a.score!));
    return scored.slice(0, 50).map((s) => s.cmd);
  }, [commands, query]);

  // Reset selection whenever the result set changes.
  useEffect(() => { setSelected(0); }, [results.length, query]);

  // Reset query when opening + focus the input.
  useEffect(() => {
    if (open) {
      setQuery('');
      // setTimeout to wait for the modal mount before focusing.
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Scroll selected item into view (keyboard nav).
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cmd-idx="${selected}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = results[selected];
        if (cmd) {
          onClose();
          cmd.run();
        }
      }
    },
    [results, selected, onClose]
  );

  if (!open) return null;

  // Group results by `group` key for visual section separation. Preserves
  // the ranking order — first time a group appears defines its position.
  const grouped: { group: string; items: Command[] }[] = [];
  results.forEach((cmd) => {
    let g = grouped.find((x) => x.group === cmd.group);
    if (!g) {
      g = { group: cmd.group, items: [] };
      grouped.push(g);
    }
    g.items.push(cmd);
  });

  let flatIdx = -1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 bg-black/30 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-2xl border theme-border bg-white dark:bg-zinc-900 shadow-2xl overflow-hidden animate-fade-in-scale"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 h-12 border-b theme-border">
          <Icon name="search" className="theme-text-muted text-sm" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search…"
            className="flex-1 bg-transparent border-0 outline-none text-sm theme-text-primary placeholder:theme-text-muted"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 h-5 rounded text-[10px] font-semibold theme-text-muted border theme-border bg-zinc-50 dark:bg-white/5">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-4 py-10 text-center theme-text-muted text-sm">
              <Icon name="search-minus" className="text-2xl opacity-50 mb-2 block" />
              No matches for "{query}"
            </div>
          ) : (
            grouped.map((section) => (
              <div key={section.group} className="mb-1">
                <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider theme-text-muted font-bold">
                  {section.group}
                </div>
                {section.items.map((cmd) => {
                  flatIdx += 1;
                  const isSelected = flatIdx === selected;
                  const idx = flatIdx;
                  return (
                    <button
                      key={cmd.id}
                      type="button"
                      data-cmd-idx={idx}
                      onMouseEnter={() => setSelected(idx)}
                      onClick={() => {
                        onClose();
                        cmd.run();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        isSelected
                          ? 'theme-accent-bg-soft theme-text-primary'
                          : 'theme-text-secondary hover:theme-text-primary'
                      }`}
                    >
                      <Icon
                        name={cmd.icon || 'fa fa-circle'}
                        className={`w-4 text-center text-[13px] ${
                          isSelected ? 'theme-accent-text' : 'theme-text-muted'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="flex-1 text-sm font-medium truncate">{cmd.label}</span>
                      {cmd.hint ? (
                        <span className="text-[11px] theme-text-muted truncate max-w-[40%]">
                          {cmd.hint}
                        </span>
                      ) : null}
                      {isSelected ? (
                        <kbd className="hidden sm:inline-flex items-center px-1.5 h-5 rounded text-[10px] font-semibold theme-text-secondary border theme-border bg-white/60 dark:bg-zinc-900/60">
                          ↵
                        </kbd>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hints */}
        <div className="px-4 h-9 border-t theme-border bg-zinc-50/60 dark:bg-white/[0.02] flex items-center justify-between text-[10px] theme-text-muted">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="inline-flex items-center px-1.5 h-4 rounded text-[10px] font-semibold border theme-border bg-white dark:bg-zinc-900">↑</kbd>
              <kbd className="inline-flex items-center px-1.5 h-4 rounded text-[10px] font-semibold border theme-border bg-white dark:bg-zinc-900">↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex items-center px-1.5 h-4 rounded text-[10px] font-semibold border theme-border bg-white dark:bg-zinc-900">↵</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex items-center px-1.5 h-4 rounded text-[10px] font-semibold border theme-border bg-white dark:bg-zinc-900">esc</kbd>
              close
            </span>
          </span>
          <span>{results.length} result{results.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}


/**
 * Global hook: opens the palette on Cmd/Ctrl+K. Render `<CommandPalette
 * open={open} onClose={...} />` somewhere stable in the tree (App root)
 * and call `useCommandPaletteShortcut(setOpen)` in the same component.
 */
export function useCommandPaletteShortcut(setOpen: (v: boolean) => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K from anywhere in the app — even inside inputs, because
      // the modifier guards against accidental triggers when typing.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    // Custom event so non-keyboard triggers (the NavMenu pill, mobile menu
    // entry, deep links) can open the palette without prop-drilling state
    // through every component. Anyone in the app can do:
    //   window.dispatchEvent(new Event('open-command-palette'))
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onOpen);
    };
  }, [setOpen]);
}

/** Helper for components that want to trigger the palette without importing
 *  the modal itself. Dispatches the same event the host listens for. */
export function openCommandPalette() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('open-command-palette'));
}
